import React, { useEffect, useState } from 'react';
import Button from './Button';
import { Cookie } from '@mui/icons-material';
import {
  CONSENT_ACCEPTED_KEY,
  CONSENT_STORAGE_KEY,
  onCookieConsentUpdated,
  type CookiePreferences,
} from '../../utils/analytics';

function readStoredPreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookiePreferences;
    return {
      necessary: true,
      analytics: parsed.analytics === true,
      marketing: false,
    };
  } catch {
    return null;
  }
}

function persistConsent(preferences: CookiePreferences) {
  localStorage.setItem(CONSENT_ACCEPTED_KEY, 'true');
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
  onCookieConsentUpdated(preferences);
  window.dispatchEvent(new CustomEvent('cookie-consent-updated'));
}

interface CookieBannerProps {
  className?: string;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ className = '' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);

  useEffect(() => {
    const cookiesAccepted = localStorage.getItem(CONSENT_ACCEPTED_KEY);
    if (!cookiesAccepted) {
      setIsVisible(true);
    } else {
      setShowIcon(true);
    }
  }, []);

  const closeBanner = () => {
    setIsVisible(false);
    setShowIcon(true);
    setIsExpanded(false);
  };

  const handleAcceptAll = () => {
    persistConsent({ necessary: true, analytics: true, marketing: false });
    closeBanner();
  };

  const handleRefuse = () => {
    persistConsent({ necessary: true, analytics: false, marketing: false });
    closeBanner();
  };

  const handleCustomizeOpen = () => {
    const stored = readStoredPreferences();
    setAnalyticsEnabled(stored?.analytics === true);
    setIsExpanded(true);
  };

  const handleSaveCustomize = () => {
    persistConsent({
      necessary: true,
      analytics: analyticsEnabled,
      marketing: false,
    });
    closeBanner();
  };

  const handleReopenSettings = () => {
    const stored = readStoredPreferences();
    setAnalyticsEnabled(stored?.analytics === true);
    setIsVisible(true);
    setShowIcon(false);
    setIsExpanded(true);
  };

  if (showIcon && !isVisible) {
    return (
      <div className={`fixed bottom-4 left-4 z-50 ${className}`}>
        <button
          type="button"
          onClick={handleReopenSettings}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 text-white shadow-soft-lg transition-all duration-300 hover:bg-primary-600 hover:shadow-soft-md active:scale-95 group"
          aria-label="Gérer les cookies"
          title="Gérer vos préférences cookies"
        >
          <Cookie className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
        </button>
      </div>
    );
  }

  if (!isVisible) {
    return null;
  }

  return (
    <div className={`fixed bottom-4 left-4 z-50 max-w-sm ${className}`}>
      <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-soft-lg">
        <div className="mb-3 flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100">
            <Cookie className="h-5 w-5 text-primary-600" />
          </div>
          <div className="flex-1">
            <h3 className="mb-1 text-sm font-semibold text-neutral-900">Cookies et mesure d&apos;audience</h3>
            <p className="text-xs leading-relaxed text-neutral-600">
              Des cookies analytiques (Google Analytics) ne sont déposés qu&apos;avec votre accord. Ils
              nous aident à comprendre l&apos;usage du site provea-immo.fr.
            </p>
          </div>
        </div>

        {isExpanded ? (
          <div className="mb-4 space-y-3">
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-neutral-800">Cookies nécessaires</span>
                <span className="text-xs text-neutral-500">Toujours actifs</span>
              </div>
              <p className="text-xs text-neutral-500">Fonctionnement du site et mémorisation de vos choix.</p>
            </div>

            <div className="flex items-center justify-between gap-3">
              <div>
                <span className="text-sm font-medium text-neutral-800">Cookies analytiques</span>
                <p className="text-xs text-neutral-500">Google Analytics 4 (pages vues, formulaires).</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analyticsEnabled}
                aria-label="Activer les cookies analytiques"
                onClick={() => setAnalyticsEnabled((v) => !v)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                  analyticsEnabled ? 'bg-primary-500' : 'bg-neutral-300'
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                    analyticsEnabled ? 'left-[1.35rem]' : 'left-0.5'
                  }`}
                />
              </button>
            </div>
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          {!isExpanded ? (
            <>
              <Button variant="primary" size="sm" onClick={handleAcceptAll} className="w-full text-xs">
                Accepter
              </Button>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" onClick={handleRefuse} className="flex-1 text-xs">
                  Refuser
                </Button>
                <Button variant="ghost" size="sm" onClick={handleCustomizeOpen} className="flex-1 text-xs">
                  Personnaliser
                </Button>
              </div>
            </>
          ) : (
            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={handleSaveCustomize} className="flex-1 text-xs">
                Enregistrer
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="flex-1 text-xs"
              >
                Retour
              </Button>
            </div>
          )}
        </div>

        <div className="mt-3 border-t border-neutral-200 pt-3">
          <button
            type="button"
            onClick={() => {
              const el = document.getElementById('contact');
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="text-xs text-primary-600 underline hover:text-primary-700"
          >
            Questions sur vos données ? Contactez-nous
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
